/**
 * Version-history slide-over, shared by the article and service editors.
 *
 * Deliberately generic: it takes a plain list of versions and a restore
 * callback rather than importing either backend's store types, so the same
 * component works for both without either depending on the other's shapes.
 */

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { fieldChanges, wordDiff, type FieldChange } from "../historyDiff"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet"
import { cn } from "@/lib/utils"
import { Bot, User, RotateCcw, Clock, ChevronDown, ChevronRight } from "lucide-react"

export interface HistoryVersionView {
  /** Stable id for the version/entry — passed back to onRestore. */
  id: string
  at: number
  /** e.g. "create", "update", "delete", "publish" — display text only. */
  action: string
  author: string
  actor: "agent" | "user"
  summary: string
  /** True when this entry is the version currently live — restore is a no-op. */
  isCurrent?: boolean
  /** True when the snapshot needed to restore this version isn't available. */
  restoreUnavailable?: boolean
  // The record as saved in this version, to show what changed from the one before
  snapshot?: Record<string, any>
}

interface HistoryPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  versions: HistoryVersionView[]
  onRestore: (versionId: string) => void
  /** Shown while a restore is in flight, to avoid double-submits. */
  restoringId?: string | null
}

const ACTION_LABEL: Record<string, string> = {
  create: "Created",
  update: "Edited",
  delete: "Deleted",
  publish: "Published",
  labels: "Labels changed",
  move: "Moved",
  translate: "Translated",
  revert: "Reverted",
}

export function HistoryPanel({ open, onOpenChange, title, versions, onRestore, restoringId }: HistoryPanelProps) {
  const [shown, setShown] = useState<string | null>(null)
  // Versions are newest first, so each one's predecessor is the next older one with a snapshot
  const changesOf = (index: number): FieldChange[] | null => {
    const v = versions[index]
    if (!v.snapshot) return null
    const prev = versions.slice(index + 1).find(o => o.snapshot)
    return fieldChanges(prev?.snapshot, v.snapshot)
  }
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b p-4">
          <SheetTitle className="text-base">Version history</SheetTitle>
          <SheetDescription className="truncate">{title}</SheetDescription>
        </SheetHeader>

        {/* Radix's inner viewport wrapper defaults to display:table, which sizes
            to its content's natural width rather than the parent's — a wide row
            (summary text + a fixed-width Restore button) then pushes the sheet
            wider than the viewport instead of wrapping. Force it to block. */}
        <ScrollArea className="flex-1 min-h-0 [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
          {versions.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No recorded changes for this item yet.</p>
          ) : (
            <div className="divide-y">
              {versions.map((v, index) => (
                <div key={v.id} className={cn("flex flex-wrap items-start gap-2 p-3", v.isCurrent && "bg-muted/40")}>
                  <div className="mt-0.5 shrink-0 text-muted-foreground">
                    {v.actor === "agent" ? <Bot className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                  </div>
                  <div className="min-w-0 flex-1 basis-40">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium">{ACTION_LABEL[v.action] ?? v.action}</span>
                      {v.isCurrent && (
                        <Badge variant="secondary" className="text-[10px]">Current</Badge>
                      )}
                    </div>
                    <p className="break-words text-xs text-muted-foreground">{v.summary}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {new Date(v.at).toLocaleString()} · {v.author}
                    </p>
                    {v.snapshot && (
                      <button
                        type="button"
                        onClick={() => setShown(shown === v.id ? null : v.id)}
                        className="mt-1 flex items-center gap-0.5 text-[11px] font-medium text-primary hover:underline"
                      >
                        {shown === v.id ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        {shown === v.id ? "Hide changes" : "Show changes"}
                      </button>
                    )}
                  </div>
                  {!v.isCurrent && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="ml-auto h-7 shrink-0 gap-1 text-xs"
                      disabled={v.restoreUnavailable || restoringId === v.id}
                      title={v.restoreUnavailable ? "This version's snapshot was trimmed and can't be restored" : "Restore this version"}
                      onClick={() => onRestore(v.id)}
                    >
                      <RotateCcw className="h-3 w-3" />
                      {restoringId === v.id ? "Restoring…" : "Restore"}
                    </Button>
                  )}
                  {shown === v.id && <Changes changes={changesOf(index)} />}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}

// Each changed field: removed words struck through, added words highlighted
function Changes({ changes }: { changes: FieldChange[] | null }) {
  if (!changes) return null
  if (!changes.length) return <p className="basis-full pl-5 text-[11px] text-muted-foreground">No field changed in this version.</p>
  return (
    <div className="basis-full space-y-2 pl-5">
      {changes.map(c => (
        <div key={c.label} className="rounded-md border bg-muted/30 p-2">
          <p className="pb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{c.label}</p>
          <p className="whitespace-pre-wrap break-words text-xs leading-relaxed">
            {!c.before && !c.after ? null : wordDiff(c.before, c.after).map((p, i) => (
              <span
                key={i}
                className={cn(
                  p.kind === "removed" && "bg-red-100 text-red-800 line-through decoration-red-400 dark:bg-red-950/40 dark:text-red-300",
                  p.kind === "added" && "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200",
                )}
              >
                {p.text}
              </span>
            ))}
            {!c.after && <span className="ml-1 text-[10px] text-muted-foreground">(cleared)</span>}
          </p>
        </div>
      ))}
    </div>
  )
}
